import ExpiredLink from '@/components/expired-link';
import FormattedMessage from '@/components/formatted-message';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { verifyMagicLinkForLoginMutationFn } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

export default function MagicLink() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token');

    const exp = Number(params.get('exp'));
    const now = Date.now();
    const isExpired = token && exp && exp <= now;

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: verifyMagicLinkForLoginMutationFn,
    });

    const handleSubmit = (e: { preventDefault: () => void }) => {
        const jwtRegex =
            /^([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_\-\\+\\/=]*)/gi;
        e.preventDefault();
        if (!token || !jwtRegex.test(token as string)) {
            toast({
                title: 'Error',
                description: 'Missing or invalid magic link token.',
                variant: 'destructive',
            });
            return;
        }
        mutate(
            { token },

            {
                onSuccess: (response) => {
                    toast({
                        title: `Hello ${response.data.user?.name ? response.data.user.name : 'Mate'},`,
                        description: (
                            <FormattedMessage message={response.data.message} />
                        ),
                        variant: 'default',
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['authUser'],
                    });
                    navigate(`/mfa`, {
                        replace: true,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        description: (
                            <FormattedMessage message={error.message} />
                        ),
                        variant: 'destructive',
                    });
                },
            },
        );
    };
    return (
        <main className='max-w-lg min-h-screen flex items-center justify-center mx-auto'>
            {isExpired ? (
                <ExpiredLink
                    title='Invalid or expired magic link'
                    message='You can log in again to send a new magic link request'
                    url='/login'
                    btnTitle='Go to login'
                />
            ) : (
                <div className='w-full h-full p-5 rounded-md'>
                    <Logo size='100px' />

                    <h1
                        className='text-fluid-h1 tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-2 mt-8
        text-center'>
                        Email Magic Link
                    </h1>
                    <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                        To login to your account, please click the button below.
                    </p>
                    <form
                        onSubmit={handleSubmit}
                        className='w-full flex items-center'>
                        <div className='flex flex-col items-center justify-center w-full px-4 md:px-16 gap-4'>
                            <Button
                                disabled={isPending}
                                type='submit'
                                className='w-full mx-auto text-[15px] h-[40px] text-white font-semibold'>
                                {isPending && (
                                    <Loader className='animate-spin' />
                                )}
                                Login by Magic Link
                            </Button>
                            <Link
                                to={'/login'}
                                className='w-full'>
                                <Button
                                    disabled={isPending}
                                    variant={'outline'}
                                    className='w-full mx-auto text-[15px] h-[40px] text-white font-semibold'>
                                    Go Back
                                </Button>
                            </Link>
                        </div>
                    </form>

                    <p className='mt-6 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                        If you have any issue accessing your account please{' '}
                        <Link
                            target='_blank'
                            rel='noreferrer'
                            className='outline-none transition duration-150 ease-in-out 
            focus-visible:ring-2 text-primary hover:underline focus-visible:ring-primary font-semibold underline-offset-4'
                            to='//x.com/_isCoding'>
                            Contact us
                        </Link>
                        .
                    </p>
                </div>
            )}
        </main>
    );
}
